import React, { FC } from 'react'

import { alpha, Box, Chip, LinearProgress, Stack, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { BigWidgetWrapper } from '../../../components/Widget/BigWidgetWrapper'
import { PsychologyType } from '../types'

type TraderProfileWidgetProps = {
  traderProfile?: PsychologyType['traderProfile']
}

export const TraderProfileWidget: FC<TraderProfileWidgetProps> = ({ traderProfile }) => {
  const { t } = useTranslation()
  const { palette } = useTheme()
  const isEmpty = !traderProfile?.label
  const profileLabelKeyMap: Record<string, string> = {
    'Disciplined Trader': 'disciplinedTrader',
    'High-Risk Impulsive Trader': 'highRiskImpulsiveTrader',
    'Emotionally Reactive Trader': 'emotionallyReactiveTrader',
    'Self-Aware but Inconsistent': 'selfAwareButInconsistent',
  }
  const profileSummaryKeyMap: Record<string, string> = {
    'Plan adherence is strong, emotional leakage is contained, and the trading process looks relatively stable.':
      'disciplinedTraderSummary',
    'Emotional tags and repeated rule breaks are strongly linked to execution risk and unstable outcomes.':
      'highRiskImpulsiveTraderSummary',
    'Psychology signals show elevated reactivity. Performance is likely being dragged by emotional decision-making.':
      'emotionallyReactiveTraderSummary',
    'Journaling signals are mixed. The trader shows useful self-awareness but execution consistency still needs work.':
      'selfAwareButInconsistentSummary',
  }
  let riskColor: 'error' | 'warning' | 'success' = 'success'

  if (traderProfile?.riskLevel === 'high') {
    riskColor = 'error'
  } else if (traderProfile?.riskLevel === 'medium') {
    riskColor = 'warning'
  }

  const metrics = [
    { key: 'disciplineScore', value: traderProfile?.disciplineScore, tone: palette.green[400] },
    {
      key: 'emotionalityScore',
      value: traderProfile?.emotionalityScore,
      tone: palette.yellow[400],
    },
    { key: 'consistencyScore', value: traderProfile?.consistencyScore, tone: palette.blue[400] },
    { key: 'planAdherenceRate', value: traderProfile?.planAdherenceRate, tone: palette.green[400] },
    {
      key: 'negativeEmotionRate',
      value: traderProfile?.negativeEmotionRate,
      tone: palette.red[400],
    },
    { key: 'ruleBreakRate', value: traderProfile?.ruleBreakRate, tone: palette.red[400] },
  ]

  return (
    <BigWidgetWrapper
      autoHeight
      fullHeight
      title={t('traderProfile', 'Trader profile')}
      subtitle={t(
        'traderProfileSubtitle',
        'A derived overview of discipline, emotionality, and execution risk.',
      )}
      isEmpty={isEmpty}
      emptyTitle={t('notEnoughData', 'Not enough data')}
      emptySubtitle={t('pleaseCompleteJournalingSection', 'Please complete "Journaling" section')}
      bgColor={palette.background.paper}
      sx={{ p: { xs: 2, sm: 2.5 } }}
    >
      <Stack spacing={0} width="100%" maxWidth="100%" minWidth={0} sx={{ boxSizing: 'border-box' }}>
        <Stack
          spacing={1.25}
          p={{ xs: 1.75, md: 2 }}
          width="100%"
          border={`1px solid ${palette.divider}`}
          borderRadius={1.5}
          bgcolor={
            palette.mode === 'dark' ? palette.background.default : alpha(palette.primary.main, 0.03)
          }
          sx={{ boxSizing: 'border-box' }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
            <Chip
              label={t(
                `psychologyPage.profile.labels.${profileLabelKeyMap[traderProfile?.label || '']}`,
                traderProfile?.label || '',
              )}
              color={riskColor}
            />
          </Stack>

          <Typography variant="body2" color="text.secondary" lineHeight={1.45}>
            {t(
              `psychologyPage.profile.summaries.${
                profileSummaryKeyMap[traderProfile?.summary || '']
              }`,
              traderProfile?.summary || '',
            )}
          </Typography>
        </Stack>

        <Box
          mt={2}
          display="grid"
          width="100%"
          maxWidth="100%"
          minWidth={0}
          overflow="hidden"
          sx={{
            boxSizing: 'border-box',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
          }}
          gap={2}
        >
          {metrics.map(({ key, value, tone }) => (
            <Stack
              key={key}
              spacing={0.75}
              p={1.75}
              border={`1px solid ${palette.divider}`}
              borderRadius={1.5}
              bgcolor={
                palette.mode === 'dark' ? palette.background.default : palette.background.paper
              }
              height="100%"
              minWidth={0}
            >
              <Stack
                direction="row"
                alignItems="baseline"
                justifyContent="space-between"
                gap={1}
                minWidth={0}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ letterSpacing: 0.4 }}
                >
                  {t(key, key)}
                </Typography>
                <Typography variant="h6" lineHeight={1.2}>
                  {value ?? 0}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, value ?? 0))}
                sx={{
                  height: 6,
                  borderRadius: 999,
                  bgcolor: alpha(tone as string, 0.14),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    backgroundColor: tone,
                  },
                }}
              />
            </Stack>
          ))}
        </Box>
      </Stack>
    </BigWidgetWrapper>
  )
}
